import type { NavGroup, NavLink, NavSection, NavTrail } from './types';

function score(link: NavLink, pathname: string): number {
  // An exact address wins outright; otherwise the longest prefix does, so
  // /sales-operations/plan lights "Plan" rather than "Today" (/sales-operations).
  if ((link.match_exact ?? []).includes(pathname)) return Number.MAX_SAFE_INTEGER;
  let best = -1;
  for (const prefix of [link.path, ...(link.match ?? [])]) {
    const hit = pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`);
    if (hit && prefix.length > best) best = prefix.length;
  }
  return best;
}

/** The row the current address belongs to, or null when no row claims it. */
export function findTrail(sections: NavSection[], pathname: string): NavTrail | null {
  let best: NavTrail | null = null;
  let bestScore = -1;
  const consider = (section: NavSection, group: NavGroup | null, link: NavLink) => {
    const s = score(link, pathname);
    if (s > bestScore) {
      bestScore = s;
      best = { section, group, link };
    }
  };
  for (const section of sections) {
    for (const item of section.items) {
      if (item.kind === 'group') item.items.forEach((link) => consider(section, item, link));
      else consider(section, null, item);
    }
  }
  return best;
}

/**
 * The sidebar as this person sees it: rows needing a capability they lack are
 * dropped, and so is any group or section left empty.
 */
export function forPerson(sections: NavSection[], can: (c: NonNullable<NavLink['requires_capability']>) => boolean): NavSection[] {
  const allowed = (n: { requires_capability?: NavLink['requires_capability'] }) =>
    !n.requires_capability || can(n.requires_capability);
  return sections
    .filter(allowed)
    .map((section) => ({
      ...section,
      items: section.items
        .filter(allowed)
        .map((item) => (item.kind === 'group' ? { ...item, items: item.items.filter(allowed) } : item))
        .filter((item) => item.kind !== 'group' || item.items.length > 0),
    }))
    .filter((section) => section.items.length > 0);
}
